import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { usePagination } from '../../../hook/usePagination';
import {
    getContestManage,
    provisionContestCategories,
    updateCampaignAwards,
    updateContest,
} from '../../../redux/contest/actions';
import { managedCampaignSelector } from '../../Contests/selector';
import { TCampCat } from '../../../redux/contest/types';
import { IContestRequest } from '../../../redux/requests/types';
import { updateRequest } from '../../../redux/requests/action';
import type { IContest } from '../../../types/global/helper';
import { contestMatchesRouteRef } from '../../../redux/contest/contestRouteMatch';

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

/** URL param may be slug; PATCH/provision require Mongo _id from loaded contest. */
function contestDocumentId(routeRef: string | undefined, contest?: IContest | null): string | undefined {
    if (contest?._id && MONGO_OBJECT_ID_RE.test(contest._id)) {
        return contest._id;
    }
    if (routeRef && MONGO_OBJECT_ID_RE.test(routeRef)) {
        return routeRef;
    }
    return contest?._id ?? routeRef;
}

export function useManageContestPage() {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const routeContestRef = params.contestId;
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [sortBy, setSortBy] = useState<'createdAt' | 'votes' | 'jury_score'>('createdAt');
    const [activeTab, setActiveTab] = useState<string>('details');

    const {
        pagination: submissionPagination,
        setPagination: setSubmissionPagination,
        handleRowsChange: handleSubmissionRowsChange,
        handlePageChange: handleSubmissionPageChange,
    } = usePagination(1, 20);

    const {
        pagination: salePagination,
        handleRowsChange: handleSaleRowsChange,
        handlePageChange: handleSalePageChange,
    } = usePagination(1, 20);

    const forceNoRefresh = useRef<boolean>(false);
    const wasUpdatingContest = useRef(false);

    const {
        managedContest,
        managedContestLoading = false,
        managedContestError,
        updatingContest,
    } = useSelector(managedCampaignSelector);

    const contestDocId = contestDocumentId(routeContestRef, managedContest?.contest);
    const contestMatchesRoute = contestMatchesRouteRef(routeContestRef, managedContest?.contest);
    /** Only block the page on first load or contest switch — not background refetches (e.g. drawer filters). */
    const isContestDetailLoading =
        (managedContestLoading && !managedContest) ||
        (!contestMatchesRoute && managedContestError == null);

    const getData = useCallback(
        async (_category?: TCampCat) => {
            if (!routeContestRef) return;

            try {
                const response: any = await dispatchPromise(
                    getContestManage({
                        contestId: routeContestRef,
                        searchTerm,
                        startDate,
                        endDate,
                        sortOrder,
                        sortBy,
                        submissionPage: submissionPagination.page,
                        submissionLimit: submissionPagination.rows,
                    }),
                );

                if (response?.data?.submissions?.pagination) {
                    const { currentPage, pageSize, totalCount } = response.data.submissions.pagination;
                    setSubmissionPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount,
                    });
                }
            } catch {
                /* saga + globalErrorHandler surface failures */
            } finally {
                forceNoRefresh.current = false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            routeContestRef,
            sortOrder,
            sortBy,
            endDate,
            submissionPagination.page,
            searchTerm,
            submissionPagination.rows,
            startDate,
            dispatchPromise,
            setSubmissionPagination,
        ],
    );

    const handleUpdateField = useCallback(
        (input: Record<string, unknown>) => {
            if (!contestDocId) return;
            dispatch(
                updateContest({
                    documentId: contestDocId,
                    payload: input,
                }),
            );
        },
        [contestDocId, dispatch],
    );

    const handleClearError = useCallback(() => {
        void getData();
    }, [getData]);

    useEffect(() => {
        void getData();
    }, [getData]);

    useEffect(() => {
        if (wasUpdatingContest.current && !updatingContest) {
            void getData();
        }
        wasUpdatingContest.current = Boolean(updatingContest);
    }, [getData, updatingContest]);

    const handleSearch = useCallback((search: string) => {
        const next = search.trim() || undefined;
        setSearchTerm(prev => (prev === next ? prev : next));
    }, []);

    const handleSort = (sort: string) => {
        const [field, order] = sort.split('_');
        setSortBy(field as 'createdAt' | 'votes' | 'jury_score');
        setSortOrder(order as 'asc' | 'desc');
    };

    const handleStartDateChange = (date: string) => setStartDate(date);
    const handleEndDateChange = (date: string) => setEndDate(date);
    const handleSortOrderChange = (order: 'asc' | 'desc') => setSortOrder(order);

    const handleClearSearch = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        void getData();
    };

    const handleClearStartDate = () => {
        forceNoRefresh.current = true;
        setStartDate(undefined);
        void getData();
    };

    const handleClearEndDate = () => {
        forceNoRefresh.current = true;
        setEndDate(undefined);
        void getData();
    };

    const handleClearAllFilters = () => {
        forceNoRefresh.current = true;
        setSearchTerm(undefined);
        setStartDate(undefined);
        setEndDate(undefined);
        setSortBy('createdAt');
        setSortOrder('desc');
        void getData();
    };

    const handleTabChange = (tab: string) => setActiveTab(tab);
    const handleEditDetails = (_sectionName: string) => {};

    const handleExportCSV = (type: 'submissions' | 'awards' | 'sales', data: unknown[]) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, type);
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(dataBlob, `${type}_export.xlsx`);
    };

    const handleOnSaveChanges = (payload: Record<string, unknown>) => {
        if (contestDocId) {
            dispatch(
                updateContest({
                    documentId: contestDocId,
                    payload,
                }),
            );
        }
    };

    const handleUpdateRequest = (request: IContestRequest, withdraw?: boolean) => {
        dispatch(
            updateRequest({
                requestId: request._id,
                resubmit: true,
                withdraw,
            }),
        );
    };

    const handleProvisionCategories = useCallback(
        async (categories: Record<string, unknown>[], categoriesEnabled: boolean) => {
            if (!contestDocId) return;
            try {
                await dispatchPromise(
                    provisionContestCategories({
                        contestId: contestDocId,
                        categories,
                        categoriesEnabled,
                    }),
                );
                await getData();
            } catch {
                throw new Error('Failed to save submission categories');
            }
        },
        [contestDocId, dispatchPromise, getData],
    );

    const handleDeleteAwardCategory = (index: number, awardId: string) => {
        if (contestDocId && managedContest?.contest?.categories) {
            const specifiedCategory = managedContest.contest.categories[index];
            if (specifiedCategory?._id) {
                dispatch(
                    updateCampaignAwards({
                        action: 'remove',
                        awardCategoryId: awardId,
                        categoryId: specifiedCategory._id,
                        contestId: contestDocId,
                    }),
                );
            }
        }
    };

    return {
        contestId: routeContestRef,
        contestDocumentId: contestDocId,
        managedContest,
        managedContestLoading,
        isContestDetailLoading,
        contestMatchesRoute,
        managedContestError,
        updatingContest,
        activeTab,
        submissionPagination,
        salePagination,
        sortOrder,
        handleSearch,
        handleSort,
        handleStartDateChange,
        handleEndDateChange,
        handleSortOrderChange,
        handleTabChange,
        handleSubmissionRowsChange,
        handleSubmissionPageChange,
        handleSaleRowsChange,
        handleSalePageChange,
        handleEditDetails,
        handleExportCSV,
        handleClearSearch,
        handleClearStartDate,
        handleClearEndDate,
        handleClearAllFilters,
        handleClearError,
        handleUpdateField,
        handleOnSaveChanges,
        handleProvisionCategories,
        handleDeleteAwardCategory,
        handleUpdateRequest,
        getData,
        setSortBy,
    };
}
